<template>
  <DefaultCrudView
    @create-new="newStatus"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="statuses"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.status_type`]="{ item }">
          <v-chip small :color="statusTypeColor(item.status_type)" dark>
            {{ $t('status_type_' + item.status_type) }}
          </v-chip>
        </template>

        <template v-slot:[`item.active`]="{ item }">
          <v-chip small :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? $t('yes') : $t('no') }}
          </v-chip>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editStatus(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item v-if="item.active" @click="toggleActive(item, false)">
                <v-list-item-icon>
                  <v-icon medium>mdi-toggle-switch-off-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('deactivate_status') }}</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.active" @click="toggleActive(item, true)">
                <v-list-item-icon>
                  <v-icon medium>mdi-toggle-switch-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('activate_status') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'

import AppActions from '@/store/app/actions-types'

import StatusAPI from '@/services/api/business/status'

export default {
  name: 'CustomerStatuses',

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
  },

  computed: {
    customerId() {
      return this.$route.params.customerId
    },

    customerName() {
      return this.$route.query.customerName || this.customerId
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: 'DeliverableSettings',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customers'),
          prependIcon: 'mdi-account-group',
          to: {
            name: 'BusinessCustomers',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customer_statuses') + (this.customerName ? ': ' + this.customerName : ''),
          prependIcon: 'mdi-flag-outline',
        },
      ]
    },

    statusTypeItems() {
      return [
        { text: this.$t('status_type_waiting'), value: 'waiting' },
        { text: this.$t('status_type_doing'), value: 'doing' },
        { text: this.$t('status_type_done'), value: 'done' },
        { text: this.$t('status_type_canceled'), value: 'canceled' },
      ]
    },

    fields() {
      if (this.selectedStatus) {
        // Edit mode: status_title + status_type + active
        return [
          {
            key: 'status_title',
            name: 'status_title',
            label: this.$t('status_title'),
            type: 'text',
            value: this.selectedStatus.status_title,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 12,
          },
          {
            key: 'status_type',
            name: 'status_type',
            label: this.$t('status_type'),
            type: 'select',
            value: this.selectedStatus.status_type,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            items: this.statusTypeItems,
            itemText: 'text',
            itemValue: 'value',
            cols: 12,
          },
          {
            key: 'active',
            name: 'active',
            label: this.$t('active'),
            type: 'switch',
            value: this.selectedStatus.active,
            cols: 12,
          },
        ]
      }

      // Create mode: status_title + status_type
      return [
        {
          key: 'status_title',
          name: 'status_title',
          label: this.$t('status_title'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 12,
        },
        {
          key: 'status_type',
          name: 'status_type',
          label: this.$t('status_type'),
          type: 'select',
          value: 'waiting',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          items: this.statusTypeItems,
          itemText: 'text',
          itemValue: 'value',
          cols: 12,
        },
      ]
    },
  },

  data() {
    return {
      loading: true,
      statuses: [],
      options: {
        sortBy: ['status_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_status',
          eventToEmit: 'create-new',
        },
      ],
      selectedStatus: null,
      headers: [
        { text: this.$t('status_title'), value: 'status_title', sortable: true },
        { text: this.$t('status_type'), value: 'status_type', sortable: true },
        { text: this.$t('active'), value: 'active', sortable: true },
        { text: this.$t('created_at'), value: 'created_at', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ],
    }
  },

  mounted() {
    this.selectedStatus = null
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    newStatus() {
      this.selectedStatus = null
      this.$refs.modal.openModal()
    },

    editStatus(status) {
      this.selectedStatus = status
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.modal) {
        console.error('Modal reference not found')
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        formData[field.name] = field.value
      })

      let apiCall

      if (this.selectedStatus) {
        const statusId = this.selectedStatus.status_id

        if (!statusId) {
          console.error('Status ID not found in selected status object:', this.selectedStatus)
          this.$refs.modal.errorMessage = this.$t('status_id_not_found')
          return
        }

        apiCall = StatusAPI.updateStatus(statusId, {
          status_title: formData.status_title,
          status_type: formData.status_type,
          active: formData.active,
        })
      } else {
        apiCall = StatusAPI.createStatus({
          customer_id: this.customerId,
          status_title: formData.status_title,
          status_type: formData.status_type,
        })
      }

      apiCall
        .then(() => {
          const message = this.selectedStatus
            ? this.$t('status_updated_successfully')
            : this.$t('status_created_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || this.$t('error')
          }
        })
    },

    toggleActive(status, activeValue) {
      const statusId = status.status_id

      if (!statusId) {
        console.error('Status ID not found:', status)
        return
      }

      StatusAPI.updateStatus(statusId, {
        status_title: status.status_title,
        status_type: status.status_type,
        active: activeValue,
      })
        .then(() => {
          const message = activeValue
            ? this.$t('status_activated_successfully')
            : this.$t('status_deactivated_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error updating status active state:', error)
        })
    },

    reloadData() {
      let options = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      // Remove customerName from query params before sending to API
      delete options.customerName

      this.loading = true
      StatusAPI.getAllStatusesByCustomer(this.customerId, options)
        .then((result) => {
          this.statuses = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    getModalTitle() {
      return this.selectedStatus ? this.$t('edit_status') : this.$t('new_status')
    },

    statusTypeColor(type) {
      const colors = {
        waiting: 'blue-grey',
        doing: 'blue',
        done: 'success',
        canceled: 'error',
      }
      return colors[type] || 'grey'
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },
  },
}
</script>
